import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { convertToNegative, isEmpty } from '../../common/misc';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    centsToFormattedDollars,
    transformBankAccountDetails,
} from '../../common/utils/bankingUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import { bankAccountDisplayDetails } from '../state/SpacesAtoms';
import {
    type AllAccountDetails,
    type BankAccountDisplayDetail,
    SyncteraAccountStatus,
} from '../types/BankAccount';
import {
    type Card,
    CardForm,
    CardStatus,
    MAX_NUMBER_PHYSICAL_CARDS,
} from '../types/Card';

const useGetAllAccountDetailsV2 = requestBuilder.query<
    AllAccountDetails,
    { userId: string }
>({
    endpoint: () => '/api/bank-accounts/all-details',
    queryKey: () => 'getAllAccountDetailsV2',
});

const useGetAllBankAccountDetails = (enabled = true) => {
    const user = useAuthedUser();

    const setBankAccountDisplayDetails = useSetAtom(bankAccountDisplayDetails);

    const {
        status,
        data: savingsDetails,
        isLoading,
        refetch: refetchAccountDetails,
        isRefetching,
    } = useGetAllAccountDetailsV2({ userId: user.id }, { enabled });

    const errorFetching = status === 'error';

    const {
        totalCurrentBalanceCents,
        totalAvailableBalanceCents,
        totalCardBalanceCents,
        totalInterestEarnedCents,
        totalCashBackEarnedCents,
        totalPendingTransfersCents,
        totalPendingCardTransactionsCents,
        bankAccountDetails: rawDetails,
    } = savingsDetails ?? {};

    const bankAccountDetails = (rawDetails ?? []).map((b) =>
        transformBankAccountDetails(b),
    );

    const transformedBankAccountDetails = bankAccountDetails.filter(
        (account) =>
            account.accountStatus === SyncteraAccountStatus.ACTIVE_OR_DISBURSED,
    );
    const closedAccountDetails = bankAccountDetails.filter(
        (account) => account.accountStatus === SyncteraAccountStatus.CLOSED,
    );

    const canRequestPhysicalCard =
        bankAccountDetails
            ?.flatMap(
                (details: BankAccountDisplayDetail) => details?.cards ?? [],
            )
            .filter(
                (card: Card) =>
                    card.form === CardForm.PHYSICAL &&
                    card.cardStatus !== CardStatus.TERMINATED &&
                    card.syncteraPersonId === user.syncteraPersonId,
            ).length < MAX_NUMBER_PHYSICAL_CARDS;

    useEffect(() => {
        // Save bank account details in global state for easy access on banking subpages
        setBankAccountDisplayDetails(transformedBankAccountDetails);
    }, [JSON.stringify(bankAccountDetails)]);

    return {
        isLoading: isLoading || isRefetching,
        errorFetching,
        bankAccountDetails: transformedBankAccountDetails,
        closedAccountDetails,
        hasAccount: isLoading ? undefined : !isEmpty(bankAccountDetails),
        refetchAccountDetails,
        totalCurrentBalance: centsToFormattedDollars(
            totalCurrentBalanceCents ?? 0,
        ),
        totalAvailableBalance: centsToFormattedDollars(
            totalAvailableBalanceCents ?? 0,
        ),
        // Display as a negative value
        totalCardBalance: centsToFormattedDollars(
            convertToNegative(totalCardBalanceCents),
        ),
        totalInterestEarned: centsToFormattedDollars(
            totalInterestEarnedCents ?? 0,
        ),
        totalCashBackEarned: centsToFormattedDollars(
            totalCashBackEarnedCents ?? 0,
        ),
        // Display as a negative value
        totalPendingTransfers: centsToFormattedDollars(
            convertToNegative(totalPendingTransfersCents),
        ),
        // Display as a negative value
        totalPendingCardTransactions: centsToFormattedDollars(
            convertToNegative(totalPendingCardTransactionsCents),
        ),
        canRequestPhysicalCard,
    };
};

export default useGetAllBankAccountDetails;
